import React from "react";
import { useRootAPI } from "@/api";
import { DownloadIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useBudgetContext } from "./useBudgetContext";
import * as Sentry from "@sentry/react";
import { GetBudgetResolutionEnum } from "@norma-bi/bi-api";
import { useT, type MessageKey } from "@/i18n";

const TEMPLATE_FILES_OPTIONS = [
  { id: GetBudgetResolutionEnum.monthly, label: "common.monthly", fileName: "monthly-budget" },
  { id: GetBudgetResolutionEnum.weekly, label: "common.weekly", fileName: "weekly-budget" },
] as const;

const getResolutionLabel = (resolution: GetBudgetResolutionEnum): MessageKey | undefined => {
  return TEMPLATE_FILES_OPTIONS.find((option) => option.id === resolution)?.label;
};

const getResolutionFileName = (resolution: GetBudgetResolutionEnum): string => {
  return TEMPLATE_FILES_OPTIONS.find((option) => option.id === resolution)?.fileName ?? resolution;
};

type TemplateDownloadProps = {
  resolution: GetBudgetResolutionEnum;
  year: number;
};

function TemplateDownloadButton({ resolution }: TemplateDownloadProps) {
  const rootAPI = useRootAPI();
  const { selectedYear, selectedBusinessId } = useBudgetContext();
  const { t } = useT();
  const label = getResolutionLabel(resolution);

  const handleDownload = async (e: React.MouseEvent) => {
    e.preventDefault();

    if (!selectedBusinessId) {
      console.error("No business selected");
      Sentry.captureException(
        new Error("Attempted to download budget template without business selected"),
        {
          tags: {
            component: "TemplateDownloadButton",
            action: "handleDownload",
          },
          extra: {
            resolution,
            selectedYear,
            businessId: selectedBusinessId,
          },
        },
      );
      return;
    }

    const requestParams = {
      resolution,
      year: selectedYear,
      business_id: selectedBusinessId,
    };

    let url: string | undefined;
    let link: HTMLAnchorElement | undefined;

    try {
      const blob = await rootAPI.getBudgetTemplate(requestParams);

      url = window.URL.createObjectURL(blob);
      link = document.createElement("a");
      link.href = url;
      link.download = `${getResolutionFileName(resolution)}-template-${selectedYear}.xlsx`;
      document.body.appendChild(link);
      link.click();
    } catch (error) {
      if (error instanceof Error) {
        Sentry.captureException(error, {
          tags: {
            component: "TemplateDownloadButton",
            action: "handleDownload",
          },
          extra: {
            resolution,
            selectedYear,
            businessId: selectedBusinessId,
            requestDetails: requestParams,
          },
        });
        console.error("Error during template download:", error);
      }
    } finally {
      if (link) {
        document.body.removeChild(link);
      }
      if (url) {
        window.URL.revokeObjectURL(url);
      }
    }
  };

  return (
    <Button type="button" variant="outline" size="sm" onClick={handleDownload}>
      <DownloadIcon data-icon="inline-start" />
      {label ? t(label) : resolution}
    </Button>
  );
}

export function TemplateDownloadSection() {
  const { selectedYear } = useBudgetContext();
  const { t } = useT();

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
      <span className="font-label">{t("budget.templates")}</span>
      <div className="flex flex-wrap items-center gap-2">
        <TemplateDownloadButton resolution={GetBudgetResolutionEnum.weekly} year={selectedYear} />
        <TemplateDownloadButton resolution={GetBudgetResolutionEnum.monthly} year={selectedYear} />
      </div>
    </div>
  );
}
